import "twin.macro";

import { Column } from "@features/ui/indexTable";
import { DemandPlanVariant } from "@models/DemandPlanVariant";
import { formatNumber } from "@utility/utilityFunctions";
import { pluralize } from "@utils/string";

import { ActionMenu } from "./DemandPlanVariantActionColumn";
import { DemandPlanVariantItemColumn } from "./DemandPlanVariantItemColumn";
import DemandPlanVariantStatusColumn from "./DemandPlanVariantStatusColumn";
import { DemandPlanVariantToOrderColumn } from "./DemandPlanVariantToOrderColumn";
import {
  ColumnTooltip,
  TotalDemandTooltipTitle,
} from "./DemandPlanVariantTooltips";

const renderNumberColumn = (v) => (
  <span tw="text-right block py-[18px]">{formatNumber(v)}</span>
);

export const columns: Column<DemandPlanVariant>[] = [
  {
    id: "status",
    label: "Status",
    sort: "status",
    className: "pr-0",
    render: (v, row) => (
      <DemandPlanVariantStatusColumn demandPlanVariant={row} />
    ),
  },
  {
    id: "_itemName",
    label: "Item",
    sort: "item",
    reverseSort: true,
    render: (_v, row) => (
      <DemandPlanVariantItemColumn demandPlanVariant={row} />
    ),
  },
  {
    id: "inventoryVelocity",
    sort: "inventory-velocity",
    reverseSort: true,
    align: "right",
    label: (
      <span>
        Monthly
        <br />
        Velocity
      </span>
    ),
    render: (v) => renderNumberColumn(Math.ceil(v)),
  },
  {
    id: "preorderQuantity",
    sort: "preorder-quantity",
    reverseSort: true,
    align: "right",
    label: <span>Pre-Order</span>,
    render: (v) => (
      <ColumnTooltip
        title={
          <span tw="text-sm">
            Current number of items on pre-orders across applicable order
            windows
          </span>
        }
      >
        {renderNumberColumn(v)}
      </ColumnTooltip>
    ),
  },
  {
    id: "leadTimeInventory",
    sort: "lead-time-inventory",
    reverseSort: true,
    align: "right",
    label: (
      <span>
        Lead Time
        <br />
        Inventory
      </span>
    ),
    render: (v, row) => (
      <ColumnTooltip
        title={
          <span tw="text-sm">{`Calculated based on a lead time of ${pluralize("day", row.leadTimeDays)}`}</span>
        }
      >
        {renderNumberColumn(v)}
      </ColumnTooltip>
    ),
  },
  {
    id: "leadTimeSafetyStock",
    sort: "lead-time-safety-stock",
    reverseSort: true,
    align: "right",
    label: (
      <span>
        Lead Time
        <br />
        Safety
        <br />
        Stock
      </span>
    ),
    render: renderNumberColumn,
    //TODO display formula?
  },
  {
    id: "demandCoverageInventory",
    sort: "demand-coverage-inventory",
    reverseSort: true,
    align: "right",
    label: (
      <span>
        Demand
        <br />
        Coverage
        <br />
        Inventory
      </span>
    ),
    render: (v, row) => (
      <ColumnTooltip
        title={
          <span tw="text-sm">{`Calculated based on a demand period of ${pluralize("day", row.demandCoverageDays)}`}</span>
        }
      >
        {renderNumberColumn(v)}
      </ColumnTooltip>
    ),
  },
  {
    id: "demandCoverageSafetyStock",
    sort: "demand-coverage-safety-stock",
    reverseSort: true,
    align: "right",
    label: (
      <span>
        Demand
        <br />
        Coverage
        <br />
        Safety Stock
      </span>
    ),
    render: renderNumberColumn,
    //TODO display formula?
  },
  {
    id: "totalDemand",
    sort: "total-demand",
    reverseSort: true,
    align: "right",
    label: (
      <span>
        Total
        <br />
        Demand
      </span>
    ),
    render: (v, row) => (
      <ColumnTooltip
        title={<TotalDemandTooltipTitle demandPlanVariant={row} />}
      >
        {renderNumberColumn(v)}
      </ColumnTooltip>
    ),
  },
  {
    id: "onHand",
    align: "right",
    label: "On-Hand",
    render: (_v, row) =>
      renderNumberColumn(row.variant.globalAvailableToOrderQty),
  },
  {
    id: "toOrder",
    sort: "purchase-order-qty",
    reverseSort: true,
    align: "right",
    label: (
      <span>
        To Be
        <br />
        Ordered
      </span>
    ),
    className: (row) => {
      // If the amount to override is different than the amount suggested to order
      return row.toOrder > 0 && row.toOrder !== row.recommendedToOrder
        ? "flagged"
        : "";
    },
    render: (_v, row) => (
      <DemandPlanVariantToOrderColumn demandPlanVariant={row} />
    ),
  },
  {
    id: "_actions",
    label: "",
    className: "align-middle pl-0 pr-1",
    render: (_v, row) => <ActionMenu demandPlanVariant={row} />,
  },
];
